export const DESTINATIONS = { issues: 'Firefighter', initiatives: 'Roadmap', inbox: 'Inbox' };
export function jiraDestination(metadata = {}) {
  const type = String(metadata.issue_type || '').trim().toLowerCase();
  if (['bug', 'bugs', 'defect'].includes(type)) return 'issues';
  if (['epic', 'initiative'].includes(type) || Number(metadata.hierarchy_level) >= 1) return 'initiatives';
  return 'inbox';
}
export function plainDescription(value) {
  if (typeof value === 'string') return value.slice(0, 16000);
  const walk = node => !node ? '' : (typeof node.text === 'string' ? node.text : (node.content || []).map(walk).join('')) +
    (['paragraph', 'heading', 'hardBreak', 'listItem'].includes(node.type) ? '\n' : '');
  return walk(value).trim().slice(0, 16000);
}
export function jiraSource(issue, { site_id, site_url }) {
  if (!issue?.key || !issue.fields || typeof issue.key !== 'string') throw new Error('The Jira export must contain issues with key and fields.');
  const fields = issue.fields;
  const site = new URL(site_url);
  if (site.protocol !== 'https:' || !site.hostname.endsWith('.atlassian.net')) throw new Error('Use the HTTPS URL of your Jira Cloud site.');
  const metadata = {
    site_id: site_id || site.origin, site_url: site.origin, key: issue.key,
    issue_type: fields.issuetype?.name || 'Unknown', hierarchy_level: fields.issuetype?.hierarchyLevel ?? null,
    parent_key: fields.parent?.key || (typeof fields.customfield_10014 === 'string' ? fields.customfield_10014 : ''),
    project: fields.project?.key || issue.key.split('-')[0], status: fields.status?.name || '',
    status_category: fields.status?.statusCategory?.key || '', priority: fields.priority?.name || '',
    assignee: fields.assignee?.displayName || '', labels: (fields.labels || []).filter(label => typeof label === 'string'),
    due_date: fields.duedate || null, source_updated_at: fields.updated || null,
  };
  return { source: 'jira', source_key: `${site.origin}:${issue.key}`, source_url: `${site.origin}/browse/${encodeURIComponent(issue.key)}`,
    title: String(fields.summary || issue.key).slice(0, 500), content: plainDescription(fields.description), metadata };
}
// Stable IDs make retries safe without relying on titles or a bounded record list.
export async function sourceId(ownerId, source, key, destination = 'source') {
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([ownerId, source, key, destination])))).slice(0, 16);
  bytes[6] = (bytes[6] & 15) | 128; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function sourcePayload(source, destination) {
  const meta = { ...source.metadata, source: source.source, source_key: source.source_key, url: source.source_url };
  const common = { title: source.title, source_metadata: meta };
  if (destination === 'inbox') return { ...common, content: source.content || source.title, source_url: source.source_url, status: 'captured' };
  if (destination === 'initiatives') return {
    ...common, description: source.content, owner: meta.assignee || '',
    status: meta.status_category === 'done' ? 'done' : meta.status_category === 'indeterminate' ? 'in_progress' : 'planned',
    time_horizon: meta.status_category === 'indeterminate' ? 'Now' : meta.status_category === 'done' ? 'Later' : 'Next',
    target_date: meta.due_date || null, priority_score: 0, tags: [...(meta.labels || []), `${source.source}-import`],
  };
  const priority = String(meta.priority || '').toLowerCase();
  const severity = /highest|critical|blocker/.test(priority) ? 'critical' : /high/.test(priority) ? 'high' : /low/.test(priority) ? 'low' : 'medium';
  return { ...common, description: source.content, status: meta.status_category === 'done' ? 'resolved' : 'new',
    source: source.source, external_id: `${source.source}:${meta.key || source.source_key}`, external_url: source.source_url,
    assignee: meta.assignee || null, labels: meta.labels || [], priority: severity, severity,
    tier: ['critical', 'high'].includes(severity) ? 1 : severity === 'medium' ? 2 : 3,
    created_date: new Date().toISOString(), updated_date: meta.source_updated_at || new Date().toISOString(),
  };
}
